import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * NewsfeedFilters - Sort/contest/post type filters component
 * Handles sort options, contest selection, and show comments toggle
 * Works for both desktop and mobile views
 */
const NewsfeedFilters = ({
	sortOptions,
	filters,
	onFiltersChange,
	defaultFilters,
	contests,
	showComments,
	onShowCommentsChange,
	getContestIndexFromIDLocal,
	getContestIDFromIndexLocal,
	onContestChange,
	userStore,
	userAuthAPI,
	isMobile = false,
}) => {
	const [showFilters, setShowFilters] = useState(false);

	const handleSortChange = (value) => {
		onFiltersChange({ ...filters, sort: parseInt(value) });
	};

	const handleContestChange = async (value) => {
		const contestIndex = parseInt(value);
		const contestID = getContestIDFromIndexLocal(contestIndex);

		onFiltersChange({ ...filters, contestid: contestID });

		// Update PrevContest if not "Glow Game" (index 0)
		if (contestID !== 0) {
			try {
				await userAuthAPI.updateMember({
					MemberID: userStore.user?.MemberID,
					PrevContest: contestID,
				});
				console.log("✅ Updated PrevContest in database:", contestID);

				// Update Zustand store to keep state in sync
				const currentUser = userStore.user;
				userStore.setUser({
					...currentUser,
					PrevContest: contestID,
				});
				console.log("✅ Updated PrevContest in Zustand store:", contestID);
			} catch (error) {
				console.error("Failed to update PrevContest:", error);
			}
		}

		// Also call the onContestChange callback if provided
		if (onContestChange) {
			onContestChange(contestID);
		}
	};

	const handleClearFilters = () => {
		if (JSON.stringify(filters) !== JSON.stringify(defaultFilters)) {
			onFiltersChange(defaultFilters);
		}
	};

	const containerClasses = isMobile
		? "overflow-hidden rounded-lg border-2 border-purple-300 bg-white shadow-lg dark:border-purple-700 dark:bg-gray-800 lg:hidden"
		: "overflow-hidden rounded-lg border-2 border-purple-300 bg-white shadow-lg dark:border-purple-700 dark:bg-gray-800";

	const headerClasses = isMobile
		? "flex w-full items-center justify-between p-3"
		: "flex w-full items-center justify-between p-4";

	const titleClasses = isMobile
		? "text-sm font-bold text-gray-900 dark:text-white"
		: "font-bold text-gray-900 dark:text-white";

	const contentClasses = isMobile ? "space-y-4 p-4 pt-4" : "space-y-4 p-4 pt-4";

	return (
		<div className={containerClasses}>
			{/* Filter Toggle Button */}
			<button
				onClick={() => setShowFilters(!showFilters)}
				className={headerClasses}
			>
				<h3 className={titleClasses}>Filters</h3>
				{showFilters ? (
					<ChevronUp
						className={
							isMobile ? "h-4 w-4 text-gray-500" : "h-5 w-5 text-gray-500"
						}
					/>
				) : (
					<ChevronDown
						className={
							isMobile ? "h-4 w-4 text-gray-500" : "h-5 w-5 text-gray-500"
						}
					/>
				)}
			</button>

			{/* Collapsible Filter Content */}
			<div
				className={`border-t border-gray-200 transition-all duration-300 ease-in-out dark:border-gray-700 ${
					showFilters
						? "max-h-[600px] opacity-100"
						: "max-h-0 border-t-0 opacity-0"
				}`}
				style={{ overflow: "hidden" }}
			>
				<div className={contentClasses}>
					{/* Show Comments Checkbox */}
					<label
						className={`flex cursor-pointer items-center gap-2 ${isMobile ? "text-sm" : ""}`}
					>
						<input
							type="checkbox"
							checked={showComments}
							onChange={(e) => onShowCommentsChange(e.target.checked)}
							className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-purple-600 accent-purple-600 focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-purple-600"
						/>
						<span
							className={
								isMobile
									? "text-gray-600 dark:text-gray-400"
									: "text-sm text-gray-700 dark:text-gray-300"
							}
						>
							Show Comments
						</span>
					</label>

					{/* Sort Filter */}
					<div className="space-y-1">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Sort By
						</label>
						<Select
							onValueChange={handleSortChange}
							value={filters.sort.toString()}
						>
							<SelectTrigger className="w-full border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
								<SelectValue placeholder="Sort" />
							</SelectTrigger>
							<SelectContent>
								{sortOptions.map((option, index) => (
									<SelectItem key={index} value={index.toString()}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Contest Filter */}
					<div className="space-y-1">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Contest
						</label>
						<Select
							onValueChange={handleContestChange}
							value={getContestIndexFromIDLocal(filters.contestid).toString()}
						>
							<SelectTrigger className="w-full border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
								<SelectValue placeholder="Contest" />
							</SelectTrigger>
							<SelectContent>
								{contests.map((contest, index) => (
									<SelectItem key={index} value={index.toString()}>
										{contest}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Clear Filters Button */}
					<Button
						variant="ghost"
						size="sm"
						className="w-full text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
						onClick={handleClearFilters}
					>
						Clear Filters
					</Button>
				</div>
			</div>
		</div>
	);
};

export default NewsfeedFilters;
